import { FC, useEffect, useRef } from 'react';
import { Box, Flex, VStack } from '@chakra-ui/react';

import { PlansVariant, ProviderPlanId } from '__generated__/GQL';
import useIsVisible from 'common/hooks/useIsVisible';
import useRect from 'common/hooks/useRect';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import { OnboardingPlanDisplay } from 'features/Onboarding/modules/PricingPlan/pages/PlanSelection/types';

import PlanOption from '../../../../../PlanOption';
import SelectedOfferTag from '../../../../../SelectedOfferTag';
import TopBar from '../TopBar';
import BulletPoints from './components/BulletPoints';
import locales from './locales';

interface PlanCardProps {
  plan: OnboardingPlanDisplay;
  icon: string;
  isFirst?: boolean;
  highlightHeight: number;
  showDetails: boolean;
  variant: PlansVariant | null;
  shouldShowPackagePriceWithVAT: boolean;
  isSelected: boolean;
  isHighlighted: boolean;
  onVisibilityChange?: (isVisible: boolean) => void;
  onChoose: (plan: OnboardingPlanDisplay) => void;
}

const PlanCard: FC<PlanCardProps> = (props) => {
  const {
    highlightHeight,
    icon,
    isFirst = false,
    isHighlighted,
    isSelected,
    onChoose,
    onVisibilityChange,
    plan,
    shouldShowPackagePriceWithVAT,
    showDetails,
    variant,
  } = props;
  // onVisibilityChange being undefined but for the first card,
  // rect and isVisible won't be use for other cards
  const containerRef = useRef<HTMLDivElement>(null);
  const [rect] = useRect(containerRef);

  const options = {
    rootMargin: '-150px',
  };
  const isVisible = useIsVisible(containerRef, options);

  useEffect(() => {
    onVisibilityChange?.(isVisible ?? true);
  }, [isVisible, onVisibilityChange]);

  const isPlanInTheMiddle =
    plan.providerPlanId === ProviderPlanId.Plus ||
    plan.providerPlanId === ProviderPlanId.MoralPersonPremium;

  return (
    <Flex
      direction="column"
      position="relative"
      {...(isFirst ? { gridColumnStart: 2 } : {})}
    >
      {(isPlanInTheMiddle || isSelected) && highlightHeight ? (
        <Box
          border="1px solid"
          borderColor={isSelected ? 'grey.800' : 'border'}
          borderRadius="radius-8"
          height={`${highlightHeight}px`}
          left={0}
          pointerEvents="none"
          position="absolute"
          right={0}
          top={0}
        ></Box>
      ) : null}
      {/* will only update for first card */}
      <TopBar.Item x={rect.left}>
        <PlanOption
          condensed
          isSelected={isSelected}
          onChoose={onChoose}
          plan={plan}
          shouldShowPackagePriceWithVAT={shouldShowPackagePriceWithVAT}
        />
      </TopBar.Item>
      <Flex
        border={!showDetails ? '1px solid' : 'none'}
        borderColor={isSelected ? 'grey.800' : 'border'}
        borderRadius="10px"
        flexDirection="column"
        paddingBottom="space-24"
        width="100%"
      >
        <VStack
          background="grey.200"
          borderTopRadius="radius-8"
          paddingTop="space-16"
          textAlign="left"
        >
          {isSelected ? (
            <SelectedOfferTag marginTop="-28px" zIndex="1" />
          ) : null}

          <Flex direction="column">
            {isHighlighted ? (
              <Text
                fontWeight="weight-500"
                left="50%"
                position="absolute"
                size="xs"
                textTransform="uppercase"
                top="space-16"
                transform="translateX(-50%)"
                variant="primary"
                whiteSpace="nowrap"
                zIndex={1}
              >
                <SunshineIcon
                  marginRight="space-4"
                  name="star"
                  size="icon-14"
                />
                {locales.recommendedTag}
              </Text>
            ) : null}

            <Flex
              height="124px"
              justifyContent="space-between"
              marginTop="space-34"
              overflow="hidden"
            >
              <Box
                backgroundImage={icon}
                backgroundPosition="center 90%"
                backgroundRepeat="no-repeat"
                backgroundSize="100%"
                height="171px"
                margin="auto"
                width="292px"
              ></Box>
            </Flex>
          </Flex>
        </VStack>
        <VStack
          alignItems="stretch"
          flex={1}
          paddingTop="space-24"
          paddingX="space-16"
          ref={onVisibilityChange ? containerRef : null}
        >
          <PlanOption
            isSelected={isSelected}
            onChoose={onChoose}
            plan={plan}
            shouldShowPackagePriceWithVAT={shouldShowPackagePriceWithVAT}
          />
        </VStack>
        <BulletPoints isMultiKYC={false} plan={plan} variant={variant} />
      </Flex>
    </Flex>
  );
};

export default PlanCard;
